import React from 'react';
import useOrganizationMedicalProfiles from '../../../../../../../hooks/api/useOrganizationMedicalProfiles';
import useOrganizationGroups from '../../../../../../../hooks/api/useOrganizationGroups';
import EditClientGroups, { useSelectedGroupIds } from '../../../../../../shared/ClientProfile/EditClientGroups';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import SideDrawerFormContainer, {
    ConfirmDirtyStateFormCloseContainer,
} from '../../../../../../v2BaseComponents/Form/SideDrawerFormContainer';
import { useOrganizationClientRecords } from '../../../../../../../hooks/api/useOrganizationClientRecords';

export type EditClientGroupsSideDrawerContentProps = {
    clientRecordId: string;
    onClose: (force: boolean) => void;
};

const EditClientGroupsSideDrawerContent: React.FC<EditClientGroupsSideDrawerContentProps> = ({
    clientRecordId,
    onClose,
}) => {
    const {
        isLoadingClientRecordGroups,
        clientRecordGroups,
        updateOrganizationClientRecordGroups,
        isUpdatingOrganizationClientRecordGroups,
        clientRecord,
    } = useOrganizationClientRecords({
        organizationClientRecordId: clientRecordId,
        onUpdateOrganizationClientRecordGroupsSuccess: () => onClose(true),
    });

    const clientProfileId = clientRecord?.status === 'active' ? clientRecord.clientMedicalProfileId : undefined;

    const { isLoadingOrganizationMedicalProfile, organizationMedicalProfile } = useOrganizationMedicalProfiles({
        medicalProfileId: clientProfileId,
    });

    const { isLoadingGroups, groups } = useOrganizationGroups({});
    const [selectedGroupIds, setSelectedGroupIds] = useSelectedGroupIds({
        clientRecordGroups: clientRecordGroups,
        organizationGroups: groups,
    });

    const isLoading =
        isLoadingOrganizationMedicalProfile ||
        isLoadingClientRecordGroups ||
        isLoadingGroups ||
        isUpdatingOrganizationClientRecordGroups;

    return (
        <AnimateLoadedContent isLoading={isLoading}>
            {() => (
                <ConfirmDirtyStateFormCloseContainer>
                    <SideDrawerFormContainer
                        onCancel={() => onClose(true)}
                        onSubmit={() => {
                            updateOrganizationClientRecordGroups({
                                clientRecordId,
                                organizationGroupIds: selectedGroupIds,
                            });
                        }}
                        isDisabled={isUpdatingOrganizationClientRecordGroups}
                    >
                        <EditClientGroups
                            clientProfile={organizationMedicalProfile || null}
                            clientRecordGroups={clientRecordGroups}
                            organizationGroups={groups}
                            selectedGroupIds={selectedGroupIds}
                            setSelectedGroupIds={setSelectedGroupIds}
                            clientFirstName={
                                organizationMedicalProfile?.contactDetails?.firstName ||
                                clientRecord?.clientDetails?.firstName ||
                                ''
                            }
                            clientLastName={
                                organizationMedicalProfile?.contactDetails?.lastName ||
                                clientRecord?.clientDetails?.lastName ||
                                ''
                            }
                        />
                    </SideDrawerFormContainer>
                </ConfirmDirtyStateFormCloseContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(EditClientGroupsSideDrawerContent);
